import { useMemo, useState } from "react";
import {
  CheckCircle2,
  CirclePause,
  Plus,
  Search,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/page-header";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  TENANT_STATUS_LABELS as sparkasseStatusLabel,
  type Tenant as Sparkasse,
} from "@nl/shared";
import { useTenants } from "@/lib/queries";
import { cn } from "@/lib/utils";
import { TenantDialog } from "@/components/admin/tenant-dialog";

import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
function PlatformSparkassenList() {
  const { data: sparkassen = [] } = useTenants();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sparkassen;
    return sparkassen.filter((s) =>
      [s.name, s.region, s.id].join(" ").toLowerCase().includes(q)
    );
  }, [query, sparkassen]);

  return (
    <div className="space-y-7">
      <PageHeader
        title="Mandanten"
        description="Sparkassen anlegen und ändern, Benutzer pflegen, Inhalte freischalten."
        actions={
          <Button className="gap-2" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" />
            Mandant anlegen
          </Button>
        }
      />

      <Card className="gap-0 overflow-hidden border-[var(--rule)] p-0">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--rule)] bg-[var(--paper)]/45 px-5 py-4">
          <div>
            <div className="eyebrow">Mandantenverwaltung</div>
            <h2 className="mt-1 font-display text-[20px] font-medium tracking-tight">
              {filtered.length} Sparkasse{filtered.length === 1 ? "" : "n"}
            </h2>
          </div>
          <div className="relative w-full sm:w-[280px]">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Sparkasse, Region, Kürzel…"
              className="h-9 pl-8 text-[14px]"
            />
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Sparkasse</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Pilotgruppen</TableHead>
              <TableHead className="text-right">Aktive Nutzer</TableHead>
              <TableHead className="text-right">Trainings · 30 Tage</TableHead>
              <TableHead className="text-right">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((s) => (
              <TableRow key={s.id}>
                <TableCell>
                  <Link to="/admin/mandanten/$id" params={{ id: s.id }}
                    className="font-medium hover:text-primary"
                  >
                    {s.name}
                  </Link>
                  {s.internal && (
                    <Badge variant="secondary" className="ml-2 rounded-sm">
                      intern
                    </Badge>
                  )}
                  <div className="text-[12.5px] text-muted-foreground">
                    {[s.region, s.id].filter(Boolean).join(" · ")}
                  </div>
                </TableCell>
                <TableCell>
                  <StatusBadge status={s.status} />
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {s.pilotTeams}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {s.activeUsers}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {s.monthlyTrainings}
                </TableCell>
                <TableCell className="text-right">
                  <Button asChild variant="ghost" size="sm">
                    <Link to="/admin/mandanten/$id" params={{ id: s.id }}>Öffnen</Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {creating && (
        <TenantDialog
          onClose={() => setCreating(false)}
          onSaved={(tenant) =>
            void navigate({ to: "/admin/mandanten/$id", params: { id: tenant.id } })
          }
        />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: Sparkasse["status"] }) {
  const active = status === "active";
  const paused = status === "paused";
  return (
    <Badge
      variant="outline"
      className={cn(
        "uppercase",
        active && "border-success/30 bg-success/10 text-success",
        paused && "bg-muted text-muted-foreground"
      )}
    >
      {paused ? <CirclePause /> : <CheckCircle2 />}
      {sparkasseStatusLabel[status]}
    </Badge>
  );
}

export const Route = createFileRoute("/_app/admin/mandanten/")({
  component: PlatformSparkassenList,
});
